import { hubConnectionManager } from '@/core/realtime';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useEffect } from 'react';

/**
 * SignalR Hook
 * Manages SignalR connection lifecycle and token expiry handling
 * HMR-safe: Does not disconnect during Vite hot reload
 */
export const useSignalRReconnector = () => {
  const { getValidToken, isAuthenticated } = useAuthStore();

  useEffect(() => {
    // Check if token is expired and handle reconnection
    const checkTokenAndConnect = async () => {
      if (!hubConnectionManager.isConnected() && isAuthenticated) {
        console.info('[useSignalr] Connecting to SignalR');
        const newToken = await getValidToken();
        if (newToken) {
          await hubConnectionManager.connect(newToken);
        }
      }
    };

    checkTokenAndConnect();

    // Cleanup only on actual app unmount (not HMR)
    // During HMR, the connection should persist
    let isUnmounting = false;

    const handleBeforeUnload = () => {
      isUnmounting = true;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Only disconnect if the page is actually unloading
      // This prevents disconnect during HMR in development
      if (isUnmounting || !import.meta.env.DEV) {
        console.info('[useSignalr] App unmounting, disconnecting...');
        hubConnectionManager.disconnect();
      } else {
        console.info('[useSignalr] HMR reload detected, keeping connection alive');
      }
    };
  }, [isAuthenticated, getValidToken]);
};
